import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_ORIGIN } from '@/store';
import defaultOgImage from '@/assets/images/logo-flowers-name-claim.png';

const formatMetaDescription = (rawText, maxLength = 155) => {
    if (!rawText) return '';

    const cleanText = rawText
        .replace(/\\"/g, '"')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();

    if (cleanText.length <= maxLength) return cleanText;

    const truncated = cleanText.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    return lastSpace > 0
        ? `${truncated.substring(0, lastSpace)}…`
        : `${truncated}…`;
};

type SEOProps = {
    title: string;
    canonicalPath: string[]; // array of path segments to be joined with slashes
    description: string;
    image?: string | null;
    imageAlt?: string;
    author?: string;
    noRobots?: boolean;
};

const SEO: FC<SEOProps> = ({
    title = 'Wir wollen Wiesen',
    canonicalPath = [],
    description,
    author = 'Richard B. King',
    image = null,
    imageAlt = 'Wir wollen Wiesen: Eine Aktion für mehr Biodiversität in Hamburg',
    noRobots,
}) => {
    const metaDescription = formatMetaDescription(description);
    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath.length ? `/${canonicalPath.join('/')}` : ''}`;
    const ogImageUrl = image ?? `${SITE_ORIGIN}${defaultOgImage}`;

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={metaDescription} />
            <meta name="author" content={author} />
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:type" content="article" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:locale" content="de_DE" />
            <meta property="og:site_name" content="Wir wollen Wiesen" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={ogImageUrl} />
            {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
            {noRobots && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
    );
};

export default SEO;
